import { useCallback, useEffect, useMemo, useState } from "react";

import {
  type Attempt,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import { ITU_CODE } from "@nav-games/morse";

import { HangmanFigure } from "../../ui/HangmanFigure";
import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface HangmanProps {
  onNavigate: (to: string) => void;
}

const MODULE = "morse";
const GAME = "hangman";
const STARTING_LIVES = 6;

/// Nautical word list — short to medium, no diacritics, all letters
/// present in ITU_CODE. Length kept modest so a session is ~30s.
/// Expand later or replace with a per-difficulty list.
const WORDS: readonly string[] = [
  "ANCHOR",
  "BEACON",
  "BOWLINE",
  "BUOY",
  "CABLE",
  "CHART",
  "COMPASS",
  "CREW",
  "DECK",
  "EBB",
  "FATHOM",
  "FLOOD",
  "FOG",
  "GALLEY",
  "HARBOUR",
  "HELM",
  "JIB",
  "KEEL",
  "KETCH",
  "KNOT",
  "LEEWARD",
  "LOG",
  "MAST",
  "MAYDAY",
  "MOORING",
  "OCEAN",
  "PASSAGE",
  "PORT",
  "REEF",
  "RIGGING",
  "SAIL",
  "SHIP",
  "SHORE",
  "SOUND",
  "STARBOARD",
  "STERN",
  "TACK",
  "TIDE",
  "WAKE",
  "WAVE",
  "WIND",
  "YACHT",
];

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function pickWord(prev?: string): string {
  // Avoid serving the same word two rounds in a row.
  let word: string;
  do {
    word = WORDS[Math.floor(Math.random() * WORDS.length)]!;
  } while (word === prev && WORDS.length > 1);
  return word;
}

function renderPattern(pattern: string): string {
  return pattern.replace(/\./g, "•").replace(/-/g, "—");
}

interface GameState {
  word: string;
  guessed: Set<string>;
  livesLeft: number;
  startedAt: number;
}

function newGame(prev?: string): GameState {
  return {
    word: pickWord(prev),
    guessed: new Set(),
    livesLeft: STARTING_LIVES,
    startedAt: Date.now(),
  };
}

function reveal(word: string, guessed: ReadonlySet<string>): string {
  return word
    .split("")
    .map((c) => (guessed.has(c) ? c : "_"))
    .join(" ");
}

function isWon(word: string, guessed: ReadonlySet<string>): boolean {
  return word.split("").every((c) => guessed.has(c));
}

export function Hangman({ onNavigate }: HangmanProps) {
  const recorder = useMemo(
    () =>
      new SessionRecorder({ module: MODULE, game: GAME, length: STARTING_LIVES }),
    [],
  );
  const [game, setGame] = useState<GameState>(() => newGame());
  const [outcome, setOutcome] = useState<"won" | "lost" | null>(null);
  const [sessionWins, setSessionWins] = useState(0);
  const [sessionRounds, setSessionRounds] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);

  const onGuess = useCallback(
    (letter: string) => {
      if (outcome || game.guessed.has(letter)) return;
      const hit = game.word.includes(letter);
      const nextGuessed = new Set(game.guessed);
      nextGuessed.add(letter);
      const nextLives = hit ? game.livesLeft : game.livesLeft - 1;
      const attempt: Omit<Attempt, "module"> = {
        char: letter,
        ok: hit,
        ms: Date.now() - game.startedAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });

      const nextState: GameState = { ...game, guessed: nextGuessed, livesLeft: nextLives };
      setGame(nextState);

      if (isWon(nextState.word, nextState.guessed)) {
        setOutcome("won");
        setSessionWins((w) => w + 1);
        setSessionRounds((r) => r + 1);
      } else if (nextLives <= 0) {
        setOutcome("lost");
        setSessionRounds((r) => r + 1);
      }
    },
    [game, outcome, recorder],
  );

  const onNext = useCallback(() => {
    setGame((g) => newGame(g.word));
    setOutcome(null);
  }, []);

  // Allow physical-keyboard input — easier to drill on a laptop.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (outcome) return;
      const k = e.key.toUpperCase();
      if (k.length === 1 && ALPHABET.includes(k)) onGuess(k);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onGuess, outcome]);

  const wrongs = [...game.guessed].filter((c) => !game.word.includes(c));
  const wrongsCount = STARTING_LIVES - game.livesLeft;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "morse" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted" style={{ textAlign: "right" }}>
        Wins this session: {sessionWins} / {sessionRounds}
      </p>

      <div
        className="card"
        style={{
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <HangmanFigure wrongs={wrongsCount} width={140} />
        <div
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 32,
            letterSpacing: "0.3em",
            margin: "12px 0",
            color:
              outcome === "lost"
                ? "var(--danger, #b8302a)"
                : outcome === "won"
                  ? "var(--ok, #2c7a3b)"
                  : undefined,
          }}
        >
          {outcome === "lost" ? game.word.split("").join(" ") : reveal(game.word, game.guessed)}
        </div>
        {wrongs.length > 0 && (
          <p className="small muted">
            Wrong: <code>{wrongs.join(" ")}</code>
          </p>
        )}
      </div>

      <p className="small muted">
        Tap a Morse pattern to guess the letter. Letters are listed under each
        pattern as a learning aid — once you know the table, scan the patterns
        first and the letters second.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
          gap: 8,
        }}
      >
        {ALPHABET.map((letter) => {
          const used = game.guessed.has(letter);
          const correct = used && game.word.includes(letter);
          return (
            <button
              key={letter}
              className="secondary"
              disabled={used || !!outcome}
              onClick={() => onGuess(letter)}
              style={{
                padding: "8px 4px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                background: correct
                  ? "rgba(44, 122, 59, 0.18)"
                  : used
                    ? "rgba(184, 48, 42, 0.12)"
                    : undefined,
              }}
            >
              <span
                style={{
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 18,
                  letterSpacing: "0.06em",
                }}
              >
                {renderPattern(ITU_CODE[letter]!)}
              </span>
              <span className="small muted">{letter}</span>
            </button>
          );
        })}
      </div>

      {outcome && (
        <div
          className="card"
          style={{ marginTop: 16, textAlign: "center" }}
        >
          <h3>
            {outcome === "won" ? "Solved!" : "Out of lives."}
            {outcome === "lost" && (
              <span className="small muted" style={{ marginLeft: 8 }}>
                ({game.word})
              </span>
            )}
          </h3>
          <div
            className="row"
            style={{ justifyContent: "center", gap: 8 }}
          >
            <button onClick={onNext}>Next word</button>
            <button
              className="secondary"
              onClick={() => onNavigate("/play/morse")}
            >
              ← Back to Morse
            </button>
          </div>
        </div>
      )}
    </>
  );
}
